import { useCallback, useEffect, useRef, useState } from 'react';

interface HoverIntentOptions {
    showDelay?: number;
    hideDelay?: number;
}

interface HoverIntentResult {
    isVisible: boolean;
    triggerProps: {
        onMouseEnter: () => void;
        onMouseLeave: () => void;
    };
    popoverProps: {
        onMouseEnter: () => void;
        onMouseLeave: () => void;
    };
}

export function useHoverIntent({ showDelay = 300, hideDelay = 150 }: HoverIntentOptions = {}): HoverIntentResult {
    const [isVisible, setIsVisible] = useState(false);
    const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimers = useCallback(() => {
        if (showTimer.current !== null) {
            clearTimeout(showTimer.current);
            showTimer.current = null;
        }
        if (hideTimer.current !== null) {
            clearTimeout(hideTimer.current);
            hideTimer.current = null;
        }
    }, []);

    const startShow = useCallback(() => {
        clearTimers();
        showTimer.current = setTimeout(() => {
            setIsVisible(true);
            showTimer.current = null;
        }, showDelay);
    }, [clearTimers, showDelay]);

    const startHide = useCallback(() => {
        if (showTimer.current !== null) {
            clearTimeout(showTimer.current);
            showTimer.current = null;
        }
        hideTimer.current = setTimeout(() => {
            setIsVisible(false);
            hideTimer.current = null;
        }, hideDelay);
    }, [hideDelay]);

    const cancelHide = useCallback(() => {
        if (hideTimer.current !== null) {
            clearTimeout(hideTimer.current);
            hideTimer.current = null;
        }
    }, []);

    useEffect(() => {
        return () => {
            clearTimers();
        };
    }, [clearTimers]);

    const triggerProps = {
        onMouseEnter: useCallback(() => {
            cancelHide();
            if (!isVisible) {
                startShow();
            }
        }, [cancelHide, isVisible, startShow]),
        onMouseLeave: useCallback(() => {
            startHide();
        }, [startHide]),
    };

    const popoverProps = {
        onMouseEnter: useCallback(() => {
            cancelHide();
        }, [cancelHide]),
        onMouseLeave: useCallback(() => {
            startHide();
        }, [startHide]),
    };

    return { isVisible, triggerProps, popoverProps };
}
